import { useMemo, useEffect, useCallback, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { object, string, number, array, boolean, InferType } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Feature, Point, MultiPolygon, Geometry } from "geojson";
import { Endgun, IrrigationSystem } from "@/types/irrigationSystem";
import { Pump, SystemType } from "@/types/pump";
import { Button, Divider, Chip } from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faLocationCrosshairs,
    faPlus,
    faTrash,
    faFloppyDisk,
    faDroplet,
    faDropletSlash,
    faX,
    faRotateLeft,
    faCircleDot,
    faChartPie,
} from "@fortawesome/free-solid-svg-icons";
import {
    IRRIGATION_SOURCE,
    IRRIGATION_FILL_LAYER,
    IRRIGATION_LINE_LAYER,
    IRRIGATION_CENTER_SOURCE,
    IRRIGATION_CENTER_LAYER,
    END_GUN_SOURCE,
    END_GUN_LAYER,
} from "@/lib/map/layers";
import {
    addUpdateGeojsonSource,
    addUpdateGeojsonLayers,
    removeLayersIfExists,
    removeSourcesIfExists,
    updateLayerVisibility,
    moveLayersToTop,
    getPointOnBoundary,
    getPointInBoundary,
} from "@/lib/utils/map";
import { getSectorGeometry, getSectorOutline } from "@/lib/utils/sector";
import { MapMouseEvent } from "mapbox-gl";
import * as turf from "@turf/turf";
import useMap from "@/lib/map/useMap";
import useRequest from "@/lib/request/useRequest";
import TextInput from "@/components/inputs/TextInput";
import NumberInput from "@/components/inputs/NumberInput";
import SectionedSelectInput from "@/components/inputs/SectionedSelectInput";
import SwitchInput from "@/components/inputs/SwitchInput";
import { isNumber } from "@/lib/utils/numbers";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { useI18n } from "@/lib/i18n/useI18n";
import { useTranslation } from "react-i18next";
import useAuth from "@/lib/auth/useAuth";


const SQ_METER_TO_ACRE = 0.000247105;
const endGunSchema = object().shape({
    start_degree: number().required().min(0).max(360),
    stop_degree: number().required().min(0).max(360),
    is_on: boolean().required().default(true),
});
const schema = object().shape({
    name: string().nullable().label("Name"),
    pivot_center_latitude: number()
        .label("Center Latitude")
        .required()
        .min(-90)
        .max(90),
    pivot_center_longitude: number().label("Center Longitude").required(),
    pivot_length: number().label("Pivot Length").required(),
    end_gun_range: number().default(0).label("End Gun Range").required(),
    pivot_start_degree: number()
        .default(0)
        .label("Start Degree")
        .required()
        .min(0)
        .max(360),
    pivot_stop_degree: number()
        .default(360)
        .label("Stop Degree")
        .required()
        .min(0)
        .max(360),
    pivot_revolution_time: number().label("Revolution Time").required().min(0),
    pivot_minimum_depth: number().label("Minimum Depth").required().min(0),
    pump: string().nullable().label("Pump"),
    end_gun_segments: array()
        .of(endGunSchema)
        .label("End Gun Sectors")
        .when("end_gun_length", {
            is: (end_gun_length: number) => end_gun_length > 0,
            then: (end_gun_segments) => end_gun_segments.required(),
            otherwise: (end_gun_segments) => end_gun_segments.nullable(),
        }),
    acres: number().nullable(),
    boundary: object()
        .shape({
            type: string().required(),
            coordinates: array().required(),
        })
        .required(),
});
type EndGunFormType = InferType<typeof endGunSchema>;
type IrrigationSystemFormType = InferType<typeof schema>;

function IrrigationSystemForm({
    fieldId,
    growerId,
    fieldBoundary,
    irrigationSystemId,
}: {
    fieldId: number | string;
    growerId?: number | string;
    fieldBoundary?: Feature<MultiPolygon>;
    irrigationSystemId?: number | string;
}) {
    const { user } = useAuth();
    const units = useMemo(() => {
        return user?.unit_preference === "metric" ? "meters" : "feet";
    }, [user?.unit_preference]);
    const { t } = useTranslation();

    const getDefaultValues = useCallback((system?: IrrigationSystem) => ({
        name: system?.name ?? undefined,
        pivot_center_latitude: system?.pivot_center?.coordinates
            ? Number(system.pivot_center.coordinates[1]?.toFixed(6))
            : undefined,
        pivot_center_longitude: system?.pivot_center?.coordinates
            ? Number(system.pivot_center.coordinates[0]?.toFixed(6))
            : undefined,
        pivot_length: system?.pivot_length ?? undefined,
        end_gun_range: system?.end_gun_range ? Number(system.end_gun_range) : 0,
        pivot_start_degree: system?.pivot_start_degree
            ? Number(system.pivot_start_degree)
            : 0,
        pivot_stop_degree: system?.pivot_stop_degree
            ? Number(system.pivot_stop_degree)
            : 360,
        pivot_revolution_time: system?.pivot_revolution_time ?? undefined,
        pivot_minimum_depth: system?.pivot_minimum_depth ?? undefined,
        pump: system?.pump?.id ? `${system.pump.id}-${system.pump.system_type}` : undefined,
        end_gun_segments: system?.end_gun_segments
            ? system.end_gun_segments.map((segment: Endgun) => ({
                start_degree: segment.start_degree,
                stop_degree: segment.stop_degree,
                is_on: segment.is_on,
            }))
            : [],
        acres: system?.acres ?? undefined,
        boundary: system?.boundary ?? undefined,
    }), []);

    const { map, mapLoaded } = useMap();
    const { useGetRequest, useMutateRequest } = useRequest();

    const irrigationSystemQuery = useGetRequest<IrrigationSystem>({
        url: `/irrigation_systems/${irrigationSystemId}/`,
        enabled: !!irrigationSystemId,
    });

    const irrigationSystem = useMemo(() => {
        return irrigationSystemQuery?.data ?? undefined;
    }, [irrigationSystemQuery.data]);

    const form = useForm<IrrigationSystemFormType>({
        resolver: yupResolver(schema),
        defaultValues: getDefaultValues(irrigationSystem),
    });

    useEffect(() => {
        form.reset(getDefaultValues(irrigationSystem));
    }, [irrigationSystem, form, getDefaultValues]);

    const [
        centerLatitude,
        centerLongitude,
        pivotLength,
        endGunRange,
        startDegree,
        stopDegree,
        endGunSegments,
        acres,
        boundary,
    ] = form.watch([
        "pivot_center_latitude",
        "pivot_center_longitude",
        "pivot_length",
        "end_gun_range",
        "pivot_start_degree",
        "pivot_stop_degree",
        "end_gun_segments",
        "acres",
        "boundary",
    ]);
    const centerPoint = useMemo(() => {
        return {
            type: "Point",
            coordinates: [Number(centerLongitude), Number(centerLatitude)],
        } as Point;
    }, [centerLatitude, centerLongitude]);
    const fullCircle = useMemo(() => {
        if (
            !pivotLength ||
            !centerLatitude ||
            !centerLongitude ||
            !isNumber(pivotLength) ||
            !isNumber(centerLatitude) ||
            !isNumber(centerLongitude)
        )
            return null;
        return getSectorGeometry(
            0,
            360,
            centerPoint,
            Number(pivotLength) + Number(endGunRange),
            fieldBoundary,
            units,
        );
    }, [
        pivotLength,
        centerLatitude,
        centerLongitude,
        fieldBoundary,
        endGunRange,
        centerPoint,
        units,
    ]);
    const endGunSegmentsString = JSON.stringify(endGunSegments);
    const [drawCenterActive, setDrawCenterActive] = useState<boolean>(false);
    const [cachedCenterPoint, setCachedCenterPoint] = useState<Point | null>(
        null,
    );
    const [drawLengthActive, setDrawLengthActive] = useState<boolean>(false);
    const [cachedLength, setCachedLength] = useState<number | null>(null);
    const [drawDegreeActive, setDrawDegreeActive] = useState<boolean>(false);
    const [cachedDegrees, setCachedDegrees] = useState<[number, number] | null>(
        null,
    );
    const [drawEndGunActive, setDrawEndGunActive] = useState<boolean>(false);
    const drawActive = useMemo(() => {
        return (
            drawCenterActive ||
            drawLengthActive ||
            drawDegreeActive ||
            drawEndGunActive
        );
    }, [
        drawCenterActive,
        drawLengthActive,
        drawDegreeActive,
        drawEndGunActive,
    ]);
    useEffect(() => {
        if (!map || !mapLoaded) return;
        if (drawActive) {
            map.getCanvas().style.cursor = "crosshair";
        } else {
            map.getCanvas().style.cursor = "";
        }
    }, [map, mapLoaded, drawActive]);

    useEffect(() => {
        if (!map || !mapLoaded) return;
        if (centerLatitude && centerLongitude) {
            addUpdateGeojsonSource(map, IRRIGATION_CENTER_SOURCE, {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: [
                        {
                            geometry: centerPoint,
                            type: "Feature",
                            properties: {},
                        },
                    ],
                },
            });
            addUpdateGeojsonLayers(map, [IRRIGATION_CENTER_LAYER]);
            updateLayerVisibility(map, [IRRIGATION_CENTER_LAYER.id], true);
        }
        if (
            !pivotLength ||
            startDegree === undefined ||
            stopDegree === undefined ||
            !isNumber(pivotLength) ||
            !isNumber(startDegree) ||
            !isNumber(stopDegree)
        ) {
            if (boundary) {
                addUpdateGeojsonSource(map, IRRIGATION_SOURCE, {
                    type: "geojson",
                    data: {
                        type: "FeatureCollection",
                        features: [{
                            geometry: boundary as unknown as Geometry,
                            type: "Feature",
                            properties: {},
                        }],
                    },
                });
                addUpdateGeojsonLayers(map, [IRRIGATION_FILL_LAYER, IRRIGATION_LINE_LAYER]);
                updateLayerVisibility(map, [IRRIGATION_FILL_LAYER.id, IRRIGATION_LINE_LAYER.id], true);
                moveLayersToTop(map, [IRRIGATION_FILL_LAYER.id, IRRIGATION_LINE_LAYER.id]);
            }
            return;
        }
        const pivotGeometry = getSectorGeometry(
            startDegree,
            stopDegree,
            centerPoint,
            Number(pivotLength) + Number(endGunRange),
            fieldBoundary,
            units,
        );
        addUpdateGeojsonSource(map, IRRIGATION_SOURCE, {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: [pivotGeometry],
            },
        });
        const endGunGeometries = endGunSegments?.map((segment) => {
            const geometry = getSectorOutline(
                segment.start_degree,
                segment.stop_degree,
                centerPoint,
                Number(pivotLength) + Number(endGunRange),
                units,
            );
            geometry.properties = {
                is_on: segment.is_on,
            };
            return geometry;
        });
        addUpdateGeojsonSource(map, END_GUN_SOURCE, {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: endGunGeometries || [],
            },
        });
        addUpdateGeojsonLayers(map, [
            IRRIGATION_FILL_LAYER,
            IRRIGATION_LINE_LAYER,
            END_GUN_LAYER,
        ]);
        updateLayerVisibility(
            map,
            [
                IRRIGATION_FILL_LAYER.id,
                IRRIGATION_LINE_LAYER.id,
                END_GUN_LAYER.id,
            ],
            true,
        );
        moveLayersToTop(map, [
            IRRIGATION_FILL_LAYER.id,
            IRRIGATION_LINE_LAYER.id,
            END_GUN_LAYER.id,
            IRRIGATION_CENTER_LAYER.id,
        ]);
        form.setValue("boundary", pivotGeometry.geometry);
        form.setValue(
            "acres",
            Math.round(turf.area(pivotGeometry) * SQ_METER_TO_ACRE),
        );
    }, [
        map,
        mapLoaded,
        endGunSegments,
        centerLatitude,
        centerLongitude,
        centerPoint,
        pivotLength,
        endGunRange,
        endGunSegmentsString,
        fieldBoundary,
        startDegree,
        stopDegree,
        form,
        units,
        boundary,
    ]);
    useEffect(() => {
        return () => {
            if (!map || !mapLoaded) return;
            removeLayersIfExists(map, [
                IRRIGATION_FILL_LAYER.id,
                IRRIGATION_LINE_LAYER.id,
                IRRIGATION_CENTER_LAYER.id,
                END_GUN_LAYER.id,
            ]);
            removeSourcesIfExists(map, [
                IRRIGATION_SOURCE,
                IRRIGATION_CENTER_SOURCE,
                END_GUN_SOURCE,
            ]);
        };
    }, [map, mapLoaded]);

    const getPumps = useGetRequest<Pump[]>({
        url: `/pumps/`,
        params: {
            grower: growerId,
        },
        enabled: !!growerId,
    });
    const pumpOptions = useMemo(() => {
        const constantPumps =
            getPumps.data
                ?.filter((pump) => pump.system_type === SystemType.constantRate)
                .map((pump) => ({
                    value: pump.id,
                    label: `${pump.pump_make} ${pump.pump_model ? pump.pump_model : "Pump"}`,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)) ?? [];
        const variablePumps =
            getPumps.data
                ?.filter((pump) => pump.system_type === SystemType.variableRate)
                .map((pump) => ({
                    value: pump.id,
                    label: `${pump.pump_make} ${pump.pump_model}`,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)) ?? [];
        return [
            {
                value: SystemType.constantRate,
                label: "Constant Rate Pumps",
                options: constantPumps,
            },
            {
                value: SystemType.variableRate,
                label: "Variable Rate Pumps",
                options: variablePumps,
            },
        ];
    }, [getPumps.data]);

    const getCenterCoordsFromMap = useCallback(
        (e: MapMouseEvent) => {
            const point = turf.point([e.lngLat.lng, e.lngLat.lat]);
            const validatedPoint = getPointInBoundary(
                point.geometry,
                fieldBoundary,
            );
            form.setValue(
                "pivot_center_latitude",
                validatedPoint.coordinates[1],
            );
            form.setValue(
                "pivot_center_longitude",
                validatedPoint.coordinates[0],
            );
        },
        [fieldBoundary, form],
    );
    const getDistanceFromMap = useCallback(
        (e: MapMouseEvent) => {
            const point = turf.point([e.lngLat.lng, e.lngLat.lat]);
            const validatedPoint = getPointInBoundary(
                point.geometry,
                fieldBoundary,
            );
            const distance = Math.round(
                turf.distance(centerPoint, validatedPoint, {
                    units: units,
                }),
            );
            form.setValue("pivot_length", distance - endGunRange || 0);
        },
        [fieldBoundary, form, centerPoint, endGunRange, units],
    );
    const getDegreeFromMap = useCallback(
        (e: MapMouseEvent): number => {
            const point = turf.point([e.lngLat.lng, e.lngLat.lat]);
            const validatedPoint = getPointOnBoundary(
                point.geometry,
                fullCircle as Feature<MultiPolygon>,
            );
            const bearing = turf.bearing(centerPoint, validatedPoint);
            return Math.round(turf.bearingToAzimuth(bearing));
        },
        [centerPoint, fullCircle],
    );

    const saveIrrigationSystem = useMutateRequest<IrrigationSystem>({
        successMessage: t("success.saved_irrigation_system", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_save_irrigation_system", {
            ns: "messages",
        }),
    });

    const { fieldQuery } = useFieldDashboard() ?? {};
    const onSave = useCallback(
        (formData: IrrigationSystemFormType) => {
            const payload = {
                ...formData,
                field: fieldId,
                pump: formData.pump
                    ? Number(formData.pump?.toString().split("-")[0])
                    : null,
                pivot_center: {
                    type: "Point",
                    coordinates: [
                        formData.pivot_center_longitude,
                        formData.pivot_center_latitude,
                    ],
                },
                pivot_start_degree: formData.pivot_start_degree,
                pivot_stop_degree: formData.pivot_stop_degree,
                end_gun_segments: formData.end_gun_segments?.map((segment) => ({
                    start_degree: segment.start_degree,
                    stop_degree: segment.stop_degree,
                    is_on: segment.is_on,
                })),
                boundary: {
                    type: "MultiPolygon",
                    coordinates:
                        formData.boundary?.type === "MultiPolygon"
                            ? formData.boundary.coordinates
                            : [formData.boundary.coordinates],
                },
            };
            saveIrrigationSystem.mutate(
                {
                    url: irrigationSystem
                        ? `/irrigation_systems/${irrigationSystem.id}/`
                        : "/irrigation_systems/",
                    method: irrigationSystem ? "PUT" : "POST",
                    data: payload,
                },
                {
                    onSuccess: (savedIrrigationSystem: IrrigationSystem) => {
                        fieldQuery?.refetch();
                        form.reset({
                            name: savedIrrigationSystem.name,
                            pivot_center_latitude: savedIrrigationSystem
                                .pivot_center?.coordinates
                                ? Number(
                                    savedIrrigationSystem.pivot_center.coordinates[1]?.toFixed(
                                        6,
                                    ),
                                )
                                : undefined,
                            pivot_center_longitude: savedIrrigationSystem
                                .pivot_center?.coordinates
                                ? Number(
                                    savedIrrigationSystem.pivot_center.coordinates[0]?.toFixed(
                                        6,
                                    ),
                                )
                                : undefined,
                            pivot_length: savedIrrigationSystem.pivot_length,
                            end_gun_range: savedIrrigationSystem.end_gun_range
                                ? Number(savedIrrigationSystem.end_gun_range)
                                : 0,
                            pivot_start_degree:
                                savedIrrigationSystem.pivot_start_degree
                                    ? Number(
                                        savedIrrigationSystem.pivot_start_degree,
                                    )
                                    : 0,
                            pivot_stop_degree:
                                savedIrrigationSystem.pivot_stop_degree
                                    ? Number(
                                        savedIrrigationSystem.pivot_stop_degree,
                                    )
                                    : 360,
                            pivot_revolution_time:
                                savedIrrigationSystem.pivot_revolution_time,
                            pivot_minimum_depth:
                                savedIrrigationSystem.pivot_minimum_depth,
                            pump: savedIrrigationSystem.pump
                                ? `${savedIrrigationSystem.pump}-${formData?.pump?.toString().split("-")[1]}`
                                : null,
                            end_gun_segments:
                                savedIrrigationSystem.end_gun_segments
                                    ? savedIrrigationSystem.end_gun_segments.map(
                                        (segment: Endgun) => ({
                                            start_degree:
                                                segment.start_degree,
                                            stop_degree: segment.stop_degree,
                                            is_on: segment.is_on,
                                        }),
                                    )
                                    : [],
                            acres: savedIrrigationSystem.acres,
                            boundary: savedIrrigationSystem.boundary,
                        });
                    },
                },
            );
        },
        [fieldId, saveIrrigationSystem, irrigationSystem, fieldQuery, form],
    );

    const deleteIrrigationSystem = useMutateRequest<void>({
        url: `/irrigation_systems/${irrigationSystem?.id}/`,
        method: "delete",
        successMessage: t("success.deleted_irrigation_system", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_delete_irrigation_system", {
            ns: "messages",
        }),
    });

    const onDelete = useCallback(() => {
        if (!irrigationSystem) return;
        deleteIrrigationSystem.mutate({}, {
            onSuccess: () => {
                fieldQuery?.refetch();
                form.reset(getDefaultValues(undefined));
                if (!map || !mapLoaded) return;
                addUpdateGeojsonSource(map, IRRIGATION_SOURCE, {
                    type: "geojson",
                    data: {
                        type: "FeatureCollection",
                        features: [],
                    },
                });
                addUpdateGeojsonSource(map, IRRIGATION_CENTER_SOURCE, {
                    type: "geojson",
                    data: {
                        type: "FeatureCollection",
                        features: [],
                    },
                });
                addUpdateGeojsonSource(map, END_GUN_SOURCE, {
                    type: "geojson",
                    data: {
                        type: "FeatureCollection",
                        features: [],
                    },
                });
            },
        });
    }, [deleteIrrigationSystem, irrigationSystem, fieldQuery, form, getDefaultValues, map, mapLoaded]);

    const { formatUnit } = useI18n();
    return (
        <FormProvider {...form}>
            <form className="flex flex-col gap-2 w-full px-2">
                <TextInput
                    name="name"
                    label={t("irrigation_system.name", {
                        ns: "setup",
                    })}
                />
                <div className="flex flex-row gap-2 w-full items-center">
                    <NumberInput
                        name="pivot_revolution_time"
                        label={t("irrigation_system.revolution_time", {
                            ns: "setup",
                        })}
                        endContent="hr"
                    />
                    <NumberInput
                        name="pivot_minimum_depth"
                        label={t("irrigation_system.minimum_depth", {
                            ns: "setup",
                        })}
                        endContent={formatUnit("in", "cm")}
                    />
                </div>
                <SectionedSelectInput
                    name="pump"
                    label={t("irrigation_system.pump", {
                        ns: "setup",
                    })}
                    groups={pumpOptions}
                />
                <div className="flex flex-row gap-2 w-full items-center">
                    <NumberInput
                        name="pivot_center_latitude"
                        label={t("irrigation_system.center_latitude", {
                            ns: "setup",
                        })}
                        size="sm"
                    />
                    <NumberInput
                        name="pivot_center_longitude"
                        label={t("irrigation_system.center_longitude", {
                            ns: "setup",
                        })}
                        size="sm"
                    />
                    {drawCenterActive ? (
                        <Button
                            color="danger"
                            onPress={() => {
                                setDrawCenterActive(false);
                                if (cachedCenterPoint) {
                                    form.setValue(
                                        "pivot_center_latitude",
                                        cachedCenterPoint?.coordinates[1],
                                    );
                                    form.setValue(
                                        "pivot_center_longitude",
                                        cachedCenterPoint?.coordinates[0],
                                    );
                                    setCachedCenterPoint(null);
                                }
                                map?.off("mousemove", getCenterCoordsFromMap);
                            }}
                        >
                            <FontAwesomeIcon icon={faX} />
                            {t("irrigation_system.cancel", {
                                ns: "setup",
                            })}
                        </Button>
                    ) : (
                        <Button
                            color="primary"
                            isDisabled={!map || !mapLoaded || drawActive}
                            onPress={() => {
                                if (!map) return;
                                setDrawCenterActive(true);
                                if (centerLatitude && centerLongitude) {
                                    setCachedCenterPoint(centerPoint);
                                }
                                map.on("mousemove", getCenterCoordsFromMap);
                                map.once("click", () => {
                                    setDrawCenterActive(false);
                                    map.off(
                                        "mousemove",
                                        getCenterCoordsFromMap,
                                    );
                                });
                            }}
                        >
                            <FontAwesomeIcon icon={faLocationCrosshairs} />
                            {t("irrigation_system.draw", {
                                ns: "setup",
                            })}
                        </Button>
                    )}
                    {cachedCenterPoint && !drawCenterActive && (
                        <Button
                            isIconOnly
                            onPress={() => {
                                form.setValue(
                                    "pivot_center_latitude",
                                    cachedCenterPoint?.coordinates[1],
                                );
                                form.setValue(
                                    "pivot_center_longitude",
                                    cachedCenterPoint?.coordinates[0],
                                );
                                setCachedCenterPoint(null);
                            }}
                        >
                            <FontAwesomeIcon icon={faRotateLeft} />
                        </Button>
                    )}
                </div>
                <div className="flex flex-row gap-2 w-full items-center">
                    <NumberInput
                        name="pivot_length"
                        label={t("irrigation_system.pivot_length", {
                            ns: "setup",
                        })}
                        endContent={formatUnit("ft", "m")}
                    />
                    <NumberInput
                        name="end_gun_range"
                        label={t("irrigation_system.end_gun_range", {
                            ns: "setup",
                        })}
                        endContent={formatUnit("ft", "m")}
                    />
                    {drawLengthActive ? (
                        <Button
                            color="danger"
                            onPress={() => {
                                setDrawLengthActive(false);
                                if (cachedLength) {
                                    form.setValue("pivot_length", cachedLength);
                                    setCachedLength(null);
                                }
                                map?.off("mousemove", getDistanceFromMap);
                            }}
                        >
                            <FontAwesomeIcon icon={faX} />
                            {t("irrigation_system.cancel", {
                                ns: "setup",
                            })}
                        </Button>
                    ) : (
                        <Button
                            color="primary"
                            isDisabled={
                                !map ||
                                !mapLoaded ||
                                !centerLatitude ||
                                !centerLongitude ||
                                drawActive
                            }
                            onPress={() => {
                                if (!map) return;
                                setDrawLengthActive(true);
                                if (pivotLength) {
                                    setCachedLength(pivotLength);
                                }
                                map.on("mousemove", getDistanceFromMap);
                                map.once("click", () => {
                                    setDrawLengthActive(false);
                                    map.off("mousemove", getDistanceFromMap);
                                });
                            }}
                        >
                            <FontAwesomeIcon icon={faCircleDot} />
                            {t("irrigation_system.draw", {
                                ns: "setup",
                            })}
                        </Button>
                    )}
                    {cachedLength && !drawLengthActive && (
                        <Button
                            isIconOnly
                            onPress={() => {
                                form.setValue("pivot_length", cachedLength);
                                setCachedLength(null);
                            }}
                        >
                            <FontAwesomeIcon icon={faRotateLeft} />
                        </Button>
                    )}
                </div>
                <div className="flex flex-row gap-2 w-full items-center">
                    <NumberInput
                        name="pivot_start_degree"
                        label={t("irrigation_system.start_degree", {
                            ns: "setup",
                        })}
                    />
                    <NumberInput
                        name="pivot_stop_degree"
                        label={t("irrigation_system.stop_degree", {
                            ns: "setup",
                        })}
                    />
                    <Button
                        color="primary"
                        isDisabled={
                            !map ||
                            !mapLoaded ||
                            !centerLatitude ||
                            !centerLongitude ||
                            !pivotLength ||
                            drawActive
                        }
                        onPress={() => {
                            if (!map) return;
                            setDrawDegreeActive(true);
                            if (startDegree !== null && stopDegree !== null) {
                                setCachedDegrees([startDegree, stopDegree]);
                            }
                            const getStartDegreeFromMap = (
                                e: MapMouseEvent,
                            ) => {
                                const degree = getDegreeFromMap(e);
                                form.setValue("pivot_start_degree", degree);
                            };
                            const getStopDegreeFromMap = (e: MapMouseEvent) => {
                                const degree = getDegreeFromMap(e);
                                form.setValue("pivot_stop_degree", degree);
                            };
                            map.on("mousemove", getStartDegreeFromMap);
                            map.once("click", () => {
                                map.off("mousemove", getStartDegreeFromMap);
                                map.on("mousemove", getStopDegreeFromMap);
                                map.once("click", () => {
                                    setDrawDegreeActive(false);
                                    map.off("mousemove", getStopDegreeFromMap);
                                });
                            });
                        }}
                    >
                        <FontAwesomeIcon icon={faChartPie} />
                        {t("irrigation_system.draw", {
                            ns: "setup",
                        })}
                    </Button>
                    {cachedDegrees && !drawDegreeActive && (
                        <Button
                            isIconOnly
                            onPress={() => {
                                form.setValue(
                                    "pivot_start_degree",
                                    cachedDegrees[0],
                                );
                                form.setValue(
                                    "pivot_stop_degree",
                                    cachedDegrees[1],
                                );
                                setCachedDegrees(null);
                            }}
                        >
                            <FontAwesomeIcon icon={faRotateLeft} />
                        </Button>
                    )}
                </div>
                <div className="flex flex-row gap-4 w-full items-center">
                    <p>
                        {t("irrigation_system.area_under_system", {
                            ns: "setup",
                        })}
                    </p>
                    <Chip color="primary" variant="flat" radius="sm" size="lg">
                        {acres ?? "-"} {formatUnit("ac", "ha")}
                    </Chip>
                </div>
                <Divider />
                <div className="flex flex-row gap-2 w-full items-center justify-between">
                    <p className="text-sm">
                        {t("irrigation_system.end_gun_sectors", {
                            ns: "setup",
                        })}
                    </p>
                    <Button
                        color="secondary"
                        size="sm"
                        onPress={() => {
                            form.setValue("end_gun_segments", [
                                ...(form.getValues("end_gun_segments") ?? []),
                                {
                                    start_degree:
                                        irrigationSystem?.pivot_start_degree ??
                                        0,
                                    stop_degree:
                                        (irrigationSystem?.pivot_start_degree ??
                                            0) + 1,
                                    is_on: true,
                                },
                            ]);
                        }}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        {t("irrigation_system.add_end_gun", {
                            ns: "setup",
                        })}
                    </Button>
                </div>
                <div className="flex flex-col gap-2 w-full px-3">
                    {form
                        .watch("end_gun_segments")
                        ?.map((segment: EndGunFormType, index: number) => (
                            <div
                                key={index}
                                className="flex flex-row gap-2 items-center"
                            >
                                <Button
                                    color="danger"
                                    isIconOnly
                                    onPress={() => {
                                        const segments =
                                            form.getValues("end_gun_segments");
                                        if (!segments) return;
                                        segments.splice(index, 1);
                                        form.setValue(
                                            "end_gun_segments",
                                            segments,
                                        );
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                                <SwitchInput
                                    name={`end_gun_segments.${index}.is_on`}
                                    defaultSelected={segment.is_on}
                                    startContent={
                                        <FontAwesomeIcon icon={faDroplet} />
                                    }
                                    endContent={
                                        <FontAwesomeIcon
                                            icon={faDropletSlash}
                                        />
                                    }
                                />
                                <NumberInput
                                    name={`end_gun_segments.${index}.start_degree`}
                                    label={t("irrigation_system.start_degree", {
                                        ns: "setup",
                                    })}
                                    size="sm"
                                    className="px-2"
                                />
                                <NumberInput
                                    name={`end_gun_segments.${index}.stop_degree`}
                                    label={t("irrigation_system.stop_degree", {
                                        ns: "setup",
                                    })}
                                    size="sm"
                                    className="px-2"
                                />
                                <Button
                                    color="primary"
                                    isDisabled={
                                        !map ||
                                        !mapLoaded ||
                                        !centerLatitude ||
                                        !centerLongitude ||
                                        !pivotLength ||
                                        drawActive
                                    }
                                    onPress={() => {
                                        if (!map) return;
                                        setDrawEndGunActive(true);
                                        const handleStartDegree = (
                                            e: MapMouseEvent,
                                        ) => {
                                            const degree = getDegreeFromMap(e);
                                            form.setValue(
                                                `end_gun_segments.${index}.start_degree`,
                                                degree,
                                            );
                                        };

                                        const handleStopDegree = (
                                            e: MapMouseEvent,
                                        ) => {
                                            const degree = getDegreeFromMap(e);
                                            form.setValue(
                                                `end_gun_segments.${index}.stop_degree`,
                                                degree,
                                            );
                                        };
                                        map.on("mousemove", handleStartDegree);
                                        map.once("click", () => {
                                            map.off(
                                                "mousemove",
                                                handleStartDegree,
                                            );
                                            map.on(
                                                "mousemove",
                                                handleStopDegree,
                                            );
                                            map.once("click", () => {
                                                setDrawEndGunActive(false);
                                                map.off(
                                                    "mousemove",
                                                    handleStopDegree,
                                                );
                                            });
                                        });
                                    }}
                                >
                                    <FontAwesomeIcon icon={faChartPie} />
                                    {t("irrigation_system.draw", {
                                        ns: "setup",
                                    })}
                                </Button>
                            </div>
                        ))}
                </div>
                <div className="flex flex-row justify-between w-full gap-3">
                    {irrigationSystem &&
                        <Button
                            color="danger"
                            fullWidth
                            onPress={onDelete}
                            isDisabled={!irrigationSystem || saveIrrigationSystem.isPending}
                            isLoading={deleteIrrigationSystem.isPending}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                            {t("delete", {
                                ns: "common",
                            })}
                        </Button>
                    }
                    <Button
                        color="secondary"
                        fullWidth
                        isLoading={saveIrrigationSystem.isPending}
                        isDisabled={saveIrrigationSystem.isPending}
                        onPress={() => {
                            form.trigger();
                            form.handleSubmit(onSave)();
                        }}
                    >
                        <FontAwesomeIcon icon={faFloppyDisk} />
                        {t("irrigation_system.save_irrigation_system", {
                            ns: "setup",
                        })}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
}

export default IrrigationSystemForm;
